import { useState } from 'react';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProcedureCodeSearch, type DentalProcedureCodeRow } from '@/hooks/useProcedureCodes';
import { cn } from '@/utils/cn';

interface ProcedureCodePickerProps {
  onSelect: (row: DentalProcedureCodeRow) => void;
  onClose?: () => void;
  selectedCode?: string | null;
  /** If true, wraps in a modal overlay */
  asModal?: boolean;
}

export function ProcedureCodePicker({
  onSelect,
  onClose,
  selectedCode,
  asModal = false,
}: ProcedureCodePickerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { data: results, isLoading } = useProcedureCodeSearch(query);

  const inner = (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="relative mb-2 flex-shrink-0">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('dental.shortcuts.pickerSearch', 'Søg ydelseskode eller navn…')}
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
        />
      </div>

      {/* Results list */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-0.5">
        {isLoading ? (
          <div className="py-6 text-center text-sm text-gray-400">{t('app.loading')}</div>
        ) : results.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-400">
            {t('dental.shortcuts.pickerNoResults', 'Ingen resultater')}
          </div>
        ) : (
          results.map((row) => (
            <button
              key={row.code}
              type="button"
              onClick={() => onSelect(row)}
              className={cn(
                'w-full rounded-lg px-3 py-2 text-left transition-colors',
                'hover:bg-indigo-50 dark:hover:bg-indigo-900/30',
                selectedCode === row.code && 'bg-indigo-50 dark:bg-indigo-900/30',
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-mono font-semibold text-indigo-600 dark:text-indigo-400 shrink-0">
                  {row.code}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                  {row.name_da}
                </span>
              </div>
              <div className="mt-0.5 flex items-center gap-2">
                <span className="text-xs text-gray-400">{row.category}</span>
                {row.default_price_dkk != null && (
                  <span className="text-xs text-gray-400">
                    {Number(row.default_price_dkk).toLocaleString('da-DK', {
                      style: 'currency',
                      currency: 'DKK',
                      maximumFractionDigits: 0,
                    })}
                  </span>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );

  if (!asModal) return inner;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
      <div className="mx-auto mt-8 flex max-w-md flex-col rounded-xl bg-white shadow-xl dark:bg-gray-900" style={{ maxHeight: '80vh' }}>
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">
            {t('dental.shortcuts.pickerTitle', 'Vælg ydelseskode')}
          </h3>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-hidden p-4">{inner}</div>
      </div>
    </div>
  );
}
